import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Radio, ArrowUpRight, Building2, Cpu, Waves, Fuel } from "lucide-react";

const INITIATIVES = [
  {
    id: "telecom-digital",
    icon: Radio,
    eyebrow: "Telecommunications & Digital Infrastructure",
    title: "Building the connective tissue of the next-generation economy.",
    body: [
      "First Providence Capital is developing strategic initiatives focused on the future of digital infrastructure — including advanced telecommunications, next-generation 5G networks, intelligent network systems, and sustainable infrastructure solutions.",
      "Working alongside institutional, government, and industry partners, we pursue opportunities that enhance connectivity, strengthen critical infrastructure, and create long-term economic value.",
      "Our approach combines innovative capital solutions with experienced execution to support projects that shape the next generation of global infrastructure.",
    ],
    pillars: [
      "Advanced telecommunications & 5G",
      "Intelligent network systems",
      "Sustainable infrastructure solutions",
      "Institutional, government & industry partnerships",
    ],
    image: "https://images.unsplash.com/photo-1772187693438-12e4b8ef3a29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxnbG9iYWwlMjBpbmZyYXN0cnVjdHVyZSUyMHNreWxpbmV8ZW58MHx8fHwxNzgzMjc1MzU3fDA&ixlib=rb-4.1.0&q=85",
  },
];

const ADJACENCIES = [
  { icon: Cpu, label: "AI Compute Infrastructure", note: "Power-anchored data campuses and sovereign AI compute." },
  { icon: Fuel, label: "Energy Transition", note: "Distributed generation, storage, and clean baseload paired with digital assets." },
  { icon: Building2, label: "Smart Cities & Real Assets", note: "Digitally-enabled urban and workforce housing developments." },
  { icon: Waves, label: "Resilient Coastal Infrastructure", note: "Long-duration infrastructure that supports connectivity and commerce." },
];

export default function StrategicInitiatives() {
  return (
    <div>
      {/* HERO */}
      <section className="relative pt-32 pb-24 bg-[#0B1F3A] text-[#F8F7F2] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.unsplash.com/photo-1772187693438-12e4b8ef3a29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxnbG9iYWwlMjBpbmZyYXN0cnVjdHVyZSUyMHNreWxpbmV8ZW58MHx8fHwxNzgzMjc1MzU3fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/60 to-[#0B1F3A]/95" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Strategic Initiatives</div>
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02] max-w-4xl">
            Purpose-built platforms for the next decade of global growth.
          </h1>
          <p className="mt-8 max-w-3xl text-[#F8F7F2]/80 text-base md:text-lg leading-relaxed">
            Beyond our core investment verticals, First Providence Capital is developing focused
            initiatives at the intersection of infrastructure, technology, and long-term economic
            value creation. Each initiative is pursued in partnership with institutional, government,
            and industry stakeholders.
          </p>
        </div>
      </section>

      {/* INITIATIVES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 space-y-24">
        {INITIATIVES.map((init, idx) => (
          <motion.article
            key={init.id}
            data-testid={`initiative-${init.id}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10"
          >
            <div className="lg:col-span-5">
              <div className="sticky top-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-14 w-14 border border-[#D4AF37] flex items-center justify-center">
                    <init.icon size={22} strokeWidth={1.4} className="text-[#0B1F3A]" />
                  </div>
                  <div className="numeral text-4xl text-[#0B1F3A] leading-none">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="eyebrow" style={{ color: "#3399FF" }}>{init.eyebrow}</div>
                <h2 className="font-display text-3xl md:text-4xl mt-3 text-[#0B1F3A] leading-tight">
                  {init.title}
                </h2>
                <div className="relative mt-8 h-56 overflow-hidden">
                  <img src={init.image} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 to-transparent" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <div className="space-y-6 text-body">
                {init.body.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              <div className="hairline my-10" />

              <div className="eyebrow" style={{ color: "#3399FF" }}>Focus Pillars</div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {init.pillars.map(p => (
                  <li key={p} className="flex gap-3 text-sm text-[#0B1F3A]">
                    <span className="text-[#D4AF37]">—</span>{p}
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 border border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-[#F8F7F2] px-6 py-3 text-xs uppercase tracking-[0.24em] font-medium transition"
                  data-testid={`initiative-cta-${init.id}`}
                >
                  Discuss This Initiative <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </motion.article>
        ))}
      </section>

      {/* ADJACENT PLATFORMS */}
      <section className="bg-[#0B1F3A] text-[#F8F7F2] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="eyebrow" style={{ color: "#D4AF37" }}>Adjacent Platforms</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                Where our initiatives connect to the broader portfolio.
              </h2>
            </div>
            <div className="md:col-span-7 flex items-end">
              <p className="text-[#F8F7F2]/75 leading-relaxed">
                Each strategic initiative is designed to reinforce — and be reinforced by — the
                firm&apos;s core investment verticals across energy, infrastructure, technology, and
                real assets. Additional initiatives will be published as they are formalized.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F8F7F2]/10">
            {ADJACENCIES.map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
                className="p-8 bg-[#0B1F3A] min-h-[220px] flex flex-col"
              >
                <a.icon size={22} strokeWidth={1.4} className="text-[#D4AF37]" />
                <div className="font-display text-xl mt-5 text-[#F8F7F2]">{a.label}</div>
                <p className="mt-3 text-sm text-[#F8F7F2]/70 leading-relaxed">{a.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
        <div className="eyebrow">Partner With Us</div>
        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-3xl mx-auto text-[#0B1F3A]">
          Institutional, government, and industry partners welcome.
        </h2>
        <p className="mt-6 text-body max-w-2xl mx-auto">
          If your organization is exploring initiatives at the intersection of connectivity,
          infrastructure, and long-term economic development, our team welcomes a confidential
          conversation.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-3 mt-10 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#c19e2f] transition"
        >
          Contact the Firm <ArrowUpRight size={16} />
        </Link>
      </section>
    </div>
  );
}
